import { useGeneratedSalesPortalBusinessPhoneNumberQuery } from '@/experiences/sales-portal/blocs/documents/sales-portal-business-phone-number.generated';
import type { ApolloError } from '@apollo/client';

interface BusinessPhoneNumberLoaderParams {
  restaurantUUID: string;
  skip?: boolean;
  supplierUUID: string;
}

interface BusinessPhoneNumberLoaderResult {
  error?: ApolloError;
  loading: boolean;
  phoneNumber: string | null;
  refetch: () => Promise<unknown>;
}

/**
 * Gets the telephone number of a business for the account and prospect
 * detail headers. The getBusinessDetails resolver calls an external API
 * (Yelp), which is why it is separate from the main detail query.
 *
 * @param params.restaurantUUID - The UUID of the restaurant.
 * @param params.skip - Skips the query when true.
 * @param params.supplierUUID - The UUID of the supplier.
 * @returns The error, loading state, a refetch and the telephone number:
 *   null if the query fails or the business has no number on file.
 */
export const useBusinessPhoneNumberLoader = ({
  restaurantUUID,
  skip,
  supplierUUID,
}: BusinessPhoneNumberLoaderParams): BusinessPhoneNumberLoaderResult => {
  const { data, error, loading, refetch } =
    useGeneratedSalesPortalBusinessPhoneNumberQuery({
      notifyOnNetworkStatusChange: true,
      skip,
      variables: { restaurantUUID, supplierUUID },
    });

  return {
    error,
    loading,
    phoneNumber: data?.businessDetails?.phone_number ?? null,
    refetch: () => refetch().catch(() => {}),
  };
};
